import {Action, Attributes, AttributesRow, Card, DataStatus} from 'platform/components';
import {Hide, Link, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil, isNotNil, not} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetBranchListQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {
  useGetTransferOrderQuery,
  useGetTransferOrderVariantQuery,
  useGetWarehousesQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {getUserName, usePermissions, useTenant} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {composePath, EMPTY_PLACEHOLDER, isNotNilOrEmpty, Nullish, parseDate} from 'shared/web';

import {BasicInformationForm} from './BasicInformationForm';
import {BasicInformationReadOnly} from './BasicInformationReadOnly';

// eslint-disable-next-line complexity -- Pre-existing violation
export function GeneralInformationTab() {
  const {transferOrderId, transferOrderVariantId} = useWarehouseParams();

  const dateTimeFormatter = useDateTimeFormatter();

  const [canEditTransferOrder] = usePermissions({
    permissionKeys: ['transferOrderEdit'],
  });

  const {tenant} = useTenant();

  const [isReadOnly, setIsReadOnly] = useState(isNotNilOrEmpty(transferOrderId));

  const transferOrderQuery = useGetTransferOrderQuery(
    {transferOrderId},
    {skip: isNilOrEmpty(transferOrderId)}
  );

  const isEditingDisabled =
    transferOrderQuery.data?.state === 'TRANSFER' || transferOrderQuery.data?.state === 'COMPLETED';

  const variantIdToFetch =
    transferOrderQuery.data?.transferOrderVariantId ?? transferOrderVariantId;

  const transferOrderVariantQuery = useGetTransferOrderVariantQuery(
    {transferOrderVariantId: variantIdToFetch as string},
    {skip: isNilOrEmpty(variantIdToFetch)}
  );

  const warehousesQuery = useGetWarehousesQuery({
    ...(transferOrderVariantQuery.data?.branchId && {
      branchId: transferOrderVariantQuery.data.branchId,
    }),
  });

  const branchListQuery = useGetBranchListQuery();

  const authorizationProfilesQuery = useGetAuthorizationProfilesQuery(
    {'x-tenant': tenant?.id as string},
    {skip: isNilOrEmpty(tenant?.id)}
  );

  const issuedByUserQuery = useGetUserQuery(
    {id: transferOrderQuery.data?.issuedBy as string},
    {skip: isNilOrEmpty(transferOrderQuery.data?.issuedBy)}
  );

  const receivedByUserQuery = useGetUserQuery(
    {id: transferOrderQuery.data?.receivedBy as string},
    {skip: isNilOrEmpty(transferOrderQuery.data?.receivedBy)}
  );

  const isLoading =
    transferOrderQuery.isLoading ||
    transferOrderVariantQuery.isLoading ||
    warehousesQuery.isLoading ||
    branchListQuery.isLoading ||
    authorizationProfilesQuery.isLoading ||
    issuedByUserQuery.isLoading ||
    receivedByUserQuery.isLoading;

  const hasError =
    transferOrderQuery.isError ||
    transferOrderVariantQuery.isError ||
    warehousesQuery.isError ||
    branchListQuery.isError ||
    authorizationProfilesQuery.isError;

  const sourceWarehouseName = warehousesQuery.data?.find(
    (warehouse) => warehouse.id === transferOrderQuery.data?.sourceWarehouseId
  )?.name;

  const destinationBranchName = branchListQuery.data?.branchListItems?.find(
    (branch) => branch.id === transferOrderQuery.data?.destinationBranchId
  )?.marketingName;

  const destinationWarehouseName = warehousesQuery.data?.find(
    (warehouse) => warehouse.id === transferOrderQuery.data?.destinationWarehouseId
  )?.name;

  const destinationAuthorizationProfileName = authorizationProfilesQuery.data?.find(
    (authorizationProfile) =>
      authorizationProfile.id === transferOrderQuery.data?.destinationAuthorizationProfileId
  )?.name;

  const formatToShortDate = (date: string | Nullish) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return dateTimeFormatter('dateShort', parseDate(date));
  };

  const basicInformationActions: Action[] = isReadOnly
    ? [
        {
          type: 'button',
          variant: 'link',
          leftIcon: 'image/edit',
          title: i18n.t('general.labels.edit'),
          isDisabled: not(canEditTransferOrder) || isEditingDisabled,
          onClick: () => setIsReadOnly(false),
        },
      ]
    : [];

  const receiveNoteAttributes: AttributesRow[] = [
    {
      label: i18n.t('general.labels.number'),
      content: isNotNil(transferOrderQuery.data?.receiveNote?.receiveNoteNumber) ? (
        <Link
          title={transferOrderQuery.data?.receiveNote?.receiveNoteNumber}
          rightIcon="action/open_in_new"
          size="small"
          onClick={() =>
            window.open(
              composePath(warehouseRoutes.receiveNoteDetailOverview, {
                params: {id: transferOrderQuery.data?.receiveNote?.receiveNoteId},
                isSearchParamsPreserved: false,
              }),
              '_blank'
            )
          }
        />
      ) : (
        EMPTY_PLACEHOLDER
      ),
    },
    {
      label: i18n.t('entity.warehouse.labels.receiptType'),
      value: match(transferOrderQuery.data?.receiveNote?.receiveNoteType)
        .with('OPENING_BALANCE', () => i18n.t('entity.warehouse.labels.openingBalance'))
        .with('PURCHASE', () => i18n.t('entity.warehouse.labels.purchase'))
        .with('TRANSFER', () => i18n.t('entity.warehouse.labels.transfer'))
        .with('EXTERNAL_TRANSFER', () => i18n.t('entity.warehouse.labels.externalTransfer'))
        .with('CORE_EXCHANGE', () => i18n.t('entity.warehouse.labels.coreExchange'))
        .otherwise(() => EMPTY_PLACEHOLDER),
    },
    {
      label: i18n.t('general.labels.createdAt'),
      value: formatToShortDate(transferOrderQuery.data?.receiveNote?.createdAt),
    },
    {
      label: i18n.t('general.labels.completedAt'),
      value: formatToShortDate(transferOrderQuery.data?.receiveNote?.completedAt),
    },
    {
      label: i18n.t('general.labels.note'),
      value: transferOrderQuery.data?.receiveNote?.note,
    },
  ];

  return (
    <DataStatus isLoading={isLoading} isError={hasError} minHeight={60}>
      <VStack spacing={4}>
        <Card
          title={i18n.t('entity.warehouse.labels.basicInformation')}
          actions={basicInformationActions}
        >
          {isReadOnly ? (
            <BasicInformationReadOnly
              readOnlyFields={{
                sourceWarehouse: sourceWarehouseName,
                destinationBranch: destinationBranchName,
                destinationWarehouse: destinationWarehouseName,
                destinationAuthorizationProfile: destinationAuthorizationProfileName,
                note: transferOrderQuery.data?.note,
                sentBy: getUserName(issuedByUserQuery.data),
                sentAt: formatToShortDate(transferOrderQuery.data?.issuedAt),
                receivedBy: getUserName(receivedByUserQuery.data),
                receivedAt: formatToShortDate(transferOrderQuery.data?.receivedAt),
              }}
            />
          ) : (
            <BasicInformationForm
              transferOrder={transferOrderQuery.data}
              warehouses={warehousesQuery.data}
              branches={branchListQuery.data}
              authorizationProfiles={authorizationProfilesQuery.data}
              onClose={() => setIsReadOnly(true)}
            />
          )}
        </Card>
        <Hide when={isNil(transferOrderQuery.data?.receiveNote)}>
          <Card title={i18n.t('entity.warehouse.labels.receiveNote')}>
            <Card variant="inlineGrey">
              <Attributes rows={receiveNoteAttributes} size="quarter" />
            </Card>
          </Card>
        </Hide>
      </VStack>
    </DataStatus>
  );
}
